import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { getPickerSources, type SourceId } from '../../lib/categoryService';
import {
    PUBMED_SOURCE_ID,
    sourceAccessById,
    type SourceAccessDecision,
} from '../../lib/sourceAccessDecision';
import { Trans } from '@lingui/react/macro';

type CategorySourceTabsProps = {
    selectedSource: SourceId;
    onSelectSource: (sourceId: SourceId) => void;
    disabled: boolean;
    decisions: SourceAccessDecision[];
    pubmedAllowed: boolean;
    accessLoading: boolean;
    accessFailed: boolean;
};

export default function CategorySourceTabs({
    selectedSource,
    onSelectSource,
    disabled,
    decisions,
    pubmedAllowed,
    accessLoading,
    accessFailed,
}: CategorySourceTabsProps) {
    const router = useRouter();
    const sources = getPickerSources(decisions);
    const accessBySource = sourceAccessById(decisions);
    const selectedDecision = accessBySource.get(selectedSource);
    const selectedSourceAllowed = selectedSource !== PUBMED_SOURCE_ID || pubmedAllowed;

    return (
        <>
            <View style={styles.sourceSwitcher}>
                {(Object.keys(sources).map(Number) as SourceId[]).map((sourceId) => {
                    const source = sources[sourceId];
                    const decision = accessBySource.get(sourceId);
                    const isSelected = selectedSource === sourceId;
                    const isRestricted = sourceId === PUBMED_SOURCE_ID;
                    const isAllowed = !isRestricted || pubmedAllowed;
                    const isInactive = isRestricted && decision?.isActive === false;
                    return (
                        <TouchableOpacity
                            key={sourceId}
                            style={[
                                styles.sourceButton,
                                isSelected && styles.sourceButtonSelected,
                                !isAllowed && styles.sourceButtonLocked,
                            ].filter(Boolean)}
                            onPress={() => onSelectSource(sourceId)}
                            disabled={disabled || isInactive}
                            accessibilityLabel={`${source.displayName}${!isAllowed ? ' locked paid source' : ''}`}
                        >
                            <Text style={[
                                styles.sourceButtonText,
                                isSelected && styles.sourceButtonTextSelected,
                            ].filter(Boolean)}>
                                {source.displayName}
                            </Text>
                            {isRestricted && !isAllowed && (
                                <View style={styles.paidBadge}>
                                    <MaterialIcons name={isInactive ? 'schedule' : 'lock'} size={11} color="#475569" />
                                    <Text style={styles.paidBadgeText}>{isInactive ? <Trans id="discovery.categorysourcetabs.unavailable" comment="Conditional UI copy in components/discovery/CategorySourceTabs.tsx">Unavailable</Trans> : <Trans id="discovery.categorysourcetabs.paid" comment="Conditional UI copy in components/discovery/CategorySourceTabs.tsx">Paid</Trans>}</Text>
                                </View>
                            )}
                        </TouchableOpacity>
                    );
                })}
            </View>

            {selectedSource === PUBMED_SOURCE_ID && !selectedSourceAllowed && (
                <View style={styles.lockedNotice}>
                    <Text style={styles.lockedNoticeText}>
                        {selectedDecision?.reason === 'source_inactive'
                            ? <Trans id="discovery.categorysourcetabs.pubmedIsTemporarilyUnavailable" comment="Conditional UI copy in components/discovery/CategorySourceTabs.tsx">PubMed is temporarily unavailable.</Trans>
                            : accessFailed
                                ? <Trans id="discovery.categorysourcetabs.pubmedAccessCouldNotBeVerifiedReloadAndTryAgain" comment="Conditional UI copy in components/discovery/CategorySourceTabs.tsx">PubMed access could not be verified. Reload and try again.</Trans>
                                : accessLoading
                                    ? <Trans id="discovery.categorysourcetabs.checkingPubmedAccess" comment="Conditional UI copy in components/discovery/CategorySourceTabs.tsx">Checking PubMed access…</Trans>
                                    : <Trans id="discovery.categorysourcetabs.pubmedIsIncludedInEveryPaidPlan" comment="Conditional UI copy in components/discovery/CategorySourceTabs.tsx">PubMed is included in every paid plan.</Trans>}
                    </Text>
                    {!accessLoading && !accessFailed && selectedDecision?.reason !== 'source_inactive' && (
                        <TouchableOpacity onPress={() => router.push('/pricing')}>
                            <Text style={styles.upgradeLink}><Trans id="discovery.categorysourcetabs.viewPlans" comment="UI copy in components/discovery/CategorySourceTabs.tsx">View plans</Trans></Text>
                        </TouchableOpacity>
                    )}
                </View>
            )}

            {selectedDecision?.reason === 'grandfathered_project' && (
                <Text style={styles.grandfatheredText}><Trans id="discovery.categorysourcetabs.pubmedIsIncludedForThisProject" comment="UI copy in components/discovery/CategorySourceTabs.tsx">PubMed is included for this project.</Trans></Text>
            )}
        </>
    );
}

const styles = StyleSheet.create({
    sourceSwitcher: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 12,
    },
    sourceButton: {
        minWidth: 112,
        minHeight: 44,
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#e2e8f0',
        backgroundColor: '#ffffff',
        alignItems: 'center',
        justifyContent: 'center',
    },
    sourceButtonSelected: {
        backgroundColor: '#e2e8f0',
        borderColor: '#64748b',
    },
    sourceButtonText: {
        fontSize: 14,
        fontWeight: '500',
        color: '#64748b',
    },
    sourceButtonTextSelected: {
        color: '#0f172a',
        fontWeight: '600',
    },
    sourceButtonLocked: {
        backgroundColor: '#f8fafc',
        borderColor: '#cbd5e1',
    },
    paidBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 3,
        marginTop: 3,
    },
    paidBadgeText: {
        color: '#475569',
        fontSize: 10,
        fontWeight: '700',
        textTransform: 'uppercase',
    },
    lockedNotice: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 6,
        borderRadius: 8,
        backgroundColor: '#f8fafc',
        padding: 12,
        marginBottom: 8,
    },
    lockedNoticeText: {
        color: '#475569',
        fontSize: 13,
    },
    upgradeLink: {
        color: '#166534',
        fontSize: 13,
        fontWeight: '700',
        textDecorationLine: 'underline',
    },
    grandfatheredText: {
        color: '#166534',
        fontSize: 12,
        marginBottom: 8,
    },
});
